// #002 + #003 + #004 — Layer 2/3/4 promptlar için ortak editör.
// - Placeholder chip strip (Layer'a göre filtrelenmiş, click → insert at cursor)
// - Karakter sayacı + Layer'a göre önerilen aralık
// - Auto-grow textarea (min 200px, max 600px)
// - Monospace font (placeholder okuma için)
//
// Settings → Sistem Prompt (Layer 1) BU komponenti kullanmaz (Q13 kararı —
// Layer 1'de placeholder yok, ayrı kompozisyon).

import { useRef, type CSSProperties } from "react";
import { PLACEHOLDER_CATALOG } from "@mahir/shared";

export type PromptLayer = "framework" | "section" | "question";

const SUGGESTED_LENGTH: Record<PromptLayer, { min: number; max: number; label: string }> = {
  framework: { min: 500, max: 1500, label: "öneri: 500–1500 karakter" },
  section: { min: 200, max: 800, label: "öneri: 200–800 karakter" },
  question: { min: 50, max: 300, label: "öneri: 50–300 karakter" },
};

// Layer'a göre geçerli placeholder'ları filtrele
function placeholdersForLayer(layer: PromptLayer): typeof PLACEHOLDER_CATALOG {
  return PLACEHOLDER_CATALOG.filter((p) => {
    const scopes = p.scope.split("/").map((s) => s.trim());
    if (layer === "framework") return scopes.includes("framework");
    if (layer === "section") return scopes.includes("section");
    return scopes.includes("question");
  });
}

interface PromptEditorProps {
  layer: PromptLayer;
  value: string;
  onChange: (val: string) => void;
  disabled?: boolean;
  placeholder?: string;
  rows?: number;
  /** Inline help: kart başlığının altına yazılır */
  hint?: string;
}

export function PromptEditor({
  layer,
  value,
  onChange,
  disabled,
  placeholder,
  rows = 8,
  hint,
}: PromptEditorProps): JSX.Element {
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const chips = placeholdersForLayer(layer);
  const suggestion = SUGGESTED_LENGTH[layer];

  // Karakter sayacı rengi: hedef aralığa göre
  const charCount = value.length;
  let countColor = "var(--color-text-3)";
  if (charCount > 0) {
    if (charCount < suggestion.min) countColor = "var(--color-warning)";
    else if (charCount > suggestion.max * 1.2) countColor = "var(--color-warning)";
    else countColor = "var(--color-success)";
  }

  function insertPlaceholder(token: string) {
    if (disabled) return;
    const ta = textareaRef.current;
    if (!ta) {
      // Fallback: append to end
      onChange(value + token);
      return;
    }
    const start = ta.selectionStart;
    const end = ta.selectionEnd;
    const before = value.slice(0, start);
    const after = value.slice(end);
    const next = before + token + after;
    onChange(next);
    // Cursor'u placeholder'ın sonuna al (microtask sonrası DOM güncellenince)
    requestAnimationFrame(() => {
      ta.focus();
      const newPos = start + token.length;
      ta.setSelectionRange(newPos, newPos);
    });
  }

  // Auto-grow: textarea içeriğine göre yükseklik hesapla (basit)
  const lineHeightPx = 22;
  const minHeight = Math.max(rows * lineHeightPx, 140);
  const lineCount = value.split("\n").length;
  const autoHeight = Math.max(minHeight, lineCount * lineHeightPx + 24);
  const cappedHeight = Math.min(autoHeight, 600);

  const textareaStyle: CSSProperties = {
    width: "100%",
    minHeight,
    height: cappedHeight,
    maxHeight: 600,
    padding: "10px 12px",
    fontFamily: "var(--font-mono)",
    fontSize: "0.85rem",
    lineHeight: 1.5,
    background: disabled ? "var(--color-bg)" : "var(--color-surface)",
    border: "1px solid var(--color-border)",
    borderRadius: 6,
    resize: "vertical",
    color: disabled ? "var(--color-text-2)" : "var(--color-text)",
  };

  return (
    <div>
      {/* Header: char counter + length suggestion */}
      <div
        className="flex items-center justify-between mb-2 text-xs"
        style={{ color: "var(--color-text-3)" }}
      >
        <span>
          <span style={{ color: countColor, fontWeight: 600 }}>{charCount}</span> karakter ·{" "}
          {suggestion.label}
        </span>
      </div>

      {hint ? (
        <p
          className="text-xs mb-2"
          style={{
            color: "var(--color-text-2)",
            fontStyle: "italic",
            lineHeight: 1.4,
          }}
        >
          {hint}
        </p>
      ) : null}

      {/* Placeholder chip strip — Layer'a göre filtreli */}
      {chips.length > 0 && (
        <div className="flex flex-wrap gap-1 mb-2" aria-label="Yapıştırılabilir placeholder'lar">
          {chips.map((p) => (
            <button
              key={p.token}
              type="button"
              onClick={() => insertPlaceholder(p.token)}
              disabled={disabled}
              title={`${p.description} · ${p.scope}\n(tıkla → imlece yapıştır)`}
              className="text-xs px-2 py-0.5 rounded font-mono disabled:opacity-50 disabled:cursor-not-allowed"
              style={{
                background: "color-mix(in srgb, var(--color-primary) 8%, white)",
                color: "var(--color-primary)",
                border: "1px solid color-mix(in srgb, var(--color-primary) 25%, transparent)",
                cursor: disabled ? "not-allowed" : "pointer",
              }}
            >
              {p.token}
            </button>
          ))}
        </div>
      )}

      <textarea
        ref={textareaRef}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        placeholder={placeholder}
        style={textareaStyle}
        aria-label={`${layer} Layer prompt`}
      />
    </div>
  );
}
